/**
 * Pure helpers for Dispatch vehicle-run queue reordering.
 * Pointer drag uses these same permutations as Up/Down.
 * Legality matches API validateReorderMoves (shared).
 */

import {
  canInitiateReorder,
  isLegalReorder,
  type RunTripSlot,
} from '@dispatchdash/shared';

export type { RunTripSlot };

export function moveTripInOrder(
  orderedIds: string[],
  tripId: string,
  direction: -1 | 1,
): string[] | null {
  const idx = orderedIds.indexOf(tripId);
  if (idx < 0) return null;
  const swap = idx + direction;
  if (swap < 0 || swap >= orderedIds.length) return null;
  const next = [...orderedIds];
  const tmp = next[idx]!;
  next[idx] = next[swap]!;
  next[swap] = tmp;
  return next;
}

/** Drop `draggedId` onto `targetId` (insert before target's former slot after removal). */
export function dropTripOnTarget(
  orderedIds: string[],
  targetId: string,
  draggedId: string | null,
): string[] | null {
  if (!draggedId || draggedId === targetId) return null;
  const from = orderedIds.indexOf(draggedId);
  const to = orderedIds.indexOf(targetId);
  if (from < 0 || to < 0) return null;
  const next = [...orderedIds];
  next.splice(from, 1);
  next.splice(to, 0, draggedId);
  return next;
}

/** Build 1-based sequence slots from the currently rendered run order + statuses. */
export function slotsFromOrderedTrips(
  orderedIds: string[],
  statusByTripId: Map<string, string> | Record<string, string>,
): RunTripSlot[] {
  const getStatus = (id: string) =>
    statusByTripId instanceof Map
      ? statusByTripId.get(id) ?? ''
      : statusByTripId[id] ?? '';
  return orderedIds.map((tripId, i) => ({
    tripId,
    sequence: i + 1,
    status: getStatus(tripId),
  }));
}

export function tripCanDrag(slots: RunTripSlot[], tripId: string): boolean {
  const slot = slots.find((s) => s.tripId === tripId);
  if (!slot) return false;
  return canInitiateReorder(slot.status);
}

export function tripCanMove(
  slots: RunTripSlot[],
  orderedIds: string[],
  tripId: string,
  direction: -1 | 1,
): boolean {
  if (!tripCanDrag(slots, tripId)) return false;
  const next = moveTripInOrder(orderedIds, tripId, direction);
  if (!next) return false;
  return isLegalReorder(slots, next);
}

export function tripCanDropOn(
  slots: RunTripSlot[],
  orderedIds: string[],
  draggedId: string | null,
  targetId: string,
): boolean {
  if (!draggedId || !tripCanDrag(slots, draggedId)) return false;
  const next = dropTripOnTarget(orderedIds, targetId, draggedId);
  if (!next) return false;
  return isLegalReorder(slots, next);
}
